import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import StatusBadge from '../components/StatusBadge'
import PlanBadge from '../components/PlanBadge'
import VideoEmbed from '../components/VideoEmbed'
import CommitCard from '../components/CommitCard'
import './Admin.css'


// Deadline hours per plan
const PLAN_DEADLINES = {
  free:       7 * 24,  // 7 days
  emerging:   7 * 24,  // 7 days
  creator:    48,
  pro:        24,
  studio:     24,
  enterprise: 24,
}

function getUrgency(proj) {
  if (!proj.submitted_at) return null
  const plan = proj.profiles?.plan || 'free'
  const deadlineHours = PLAN_DEADLINES[plan] ?? 7 * 24
  const submittedAt = new Date(proj.submitted_at)
  const now = new Date()
  const hoursElapsed = (now - submittedAt) / (1000 * 60 * 60)
  const hoursLeft = deadlineHours - hoursElapsed
  if (hoursLeft < 0)   return { level: 'overdue',  label: 'Overdue',   hoursLeft }
  if (hoursLeft < 6)   return { level: 'critical', label: 'Due soon',  hoursLeft }
  if (hoursLeft < 24)  return { level: 'warning',  label: 'Due today', hoursLeft }
  return null
}

function sortByUrgency(projects) {
  return [...projects].sort((a, b) => {
    const ua = getUrgency(a)
    const ub = getUrgency(b)
    const hoursA = ua ? ua.hoursLeft : Infinity
    const hoursB = ub ? ub.hoursLeft : Infinity
    return hoursA - hoursB
  })
}

const TABS = ['submitted', 'needs_evidence', 'verified', 'rejected', 'all']

export default function Admin() {
  const { user, profile, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState('submitted')
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [commits, setCommits] = useState([])
  const [commitsLoading, setCommitsLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [stats, setStats] = useState({})
  const [wallCommits, setWallCommits] = useState([])
  const [wallLoading, setWallLoading] = useState(false)

  useEffect(() => {
    if (!authLoading && (!user || !profile?.is_admin)) { navigate('/'); return }
    if (!authLoading && profile?.is_admin) { fetchProjects(); fetchStats() }
  }, [user, profile, authLoading]) // eslint-disable-line

  useEffect(() => {
    if (!profile?.is_admin) return
    if (tab === 'wall') fetchWallCommits()
    else fetchProjects()
  }, [tab]) // eslint-disable-line

  async function fetchWallCommits() {
    setWallLoading(true)
    const { data } = await supabase
      .from('commits')
      .select(`
        *,
        profiles!commits_user_id_fkey(username, display_name, full_name, avatar_url),
        projects!commits_project_id_fkey(id, title, status, visibility)
      `)
      .or('dispute_status.eq.disputed,is_removed.eq.true')
      .order('created_at', { ascending: false })
      .limit(50)
    setWallCommits(data || [])
    setWallLoading(false)
  }

  async function fetchStats() {
    const { data } = await supabase.from('projects').select('status')
    const s = { submitted: 0, needs_evidence: 0, verified: 0, rejected: 0, all: 0 }
    data?.forEach(r => { s[r.status] = (s[r.status] || 0) + 1; s.all++ })
    setStats(s)
  }

  async function fetchProjects() {
    setLoading(true)
    let q = supabase.from('projects').select('*, profiles!projects_user_id_fkey(full_name, display_name, email, plan)').order('submitted_at', { ascending: true, nullsFirst: false })
    if (tab !== 'all') q = q.eq('status', tab)
    const { data } = await q
    setProjects(sortByUrgency(data || []))
    setLoading(false)
  }

  async function selectProject(proj) {
    setSelected(proj)
    setMessage('')
    setCommitsLoading(true)
    const { data } = await supabase.from('commits').select('*').eq('project_id', proj.id).order('created_at', { ascending: true })
    setCommits(data || [])
    setCommitsLoading(false)
  }

  async function handleAction(action) {
    setActionLoading(true)
    const updates = { updated_at: new Date().toISOString() }
    if (action === 'verify') {
      updates.status = 'verified'
      updates.verified_at = new Date().toISOString()
      updates.verified_by_name = profile.display_name || profile.full_name || user.email
      updates.verified_by_id = user.id
      updates.admin_message = message || null
    } else if (action === 'reject') {
      updates.status = 'rejected'
      updates.admin_message = message || 'Your submission did not meet our verification criteria.'
    } else if (action === 'needs_evidence') {
      updates.status = 'needs_evidence'
      updates.admin_message = message || 'Please provide additional evidence of your creative process.'
    }
    await supabase.from('projects').update(updates).eq('id', selected.id)
    setSelected(null)
    setMessage('')
    fetchProjects()
    fetchStats()
    setActionLoading(false)
  }

  if (authLoading) return <div style={{ textAlign: 'center', padding: 80 }}><span className="loading-spinner" /></div>
  if (!profile?.is_admin) return null

  return (
    <div className="admin-page page-fade">
      <div className="container">
        <div className="admin-header">
          <div>
            <h1>Admin panel</h1>
            <p>Review and verify human origin projects</p>
          </div>
          <Link to="/admin/settings" className="btn btn-secondary btn-sm">Reviewer profile →</Link>
        </div>

        <div className="admin-stats">
          {TABS.map(t => (
            <button key={t} className={`admin-stat-btn ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
              <span className="admin-stat-num">{stats[t] || 0}</span>
              <span className="admin-stat-label">{t.replace('_', ' ')}</span>
            </button>
          ))}
          <button className={`admin-stat-btn ${tab === 'wall' ? 'active' : ''}`} onClick={() => setTab('wall')}>
            <span className="admin-stat-num">{wallCommits.length}</span>
            <span className="admin-stat-label">wall (flagged)</span>
          </button>
        </div>

        {tab === 'wall' ? (
          <div className="admin-layout">
            <div className="admin-list" style={{ maxWidth: 640 }}>
              {wallLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}><span className="loading-spinner" /></div>
              ) : wallCommits.length === 0 ? (
                <div className="admin-empty">No disputed or removed commits right now.</div>
              ) : (
                wallCommits.map(c => <CommitCard key={c.id} commit={c} onChange={fetchWallCommits} />)
              )}
            </div>
          </div>
        ) : (
        <div className="admin-layout">
          <div className="admin-list">
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><span className="loading-spinner" /></div>
            ) : projects.length === 0 ? (
              <div className="admin-empty">No projects in this category.</div>
            ) : projects.map(proj => (
              <div key={proj.id} className={`admin-row ${selected?.id === proj.id ? 'selected' : ''} ${getUrgency(proj)?.level ? `urgency-${getUrgency(proj).level}` : ''}`} onClick={() => selectProject(proj)}>
                <div className="admin-row-info">
                  <div className="admin-row-title">
                    {proj.title}
                    {getUrgency(proj) && (
                      <span className={`urgency-tag urgency-tag-${getUrgency(proj).level}`}>
                        {getUrgency(proj).label}
                      </span>
                    )}
                  </div>
                  <div className="admin-row-meta">
                    <span>{proj.profiles?.display_name || proj.profiles?.full_name || 'Unknown'}</span>
                    <span>·</span>
                    <span className={`admin-plan-pill plan-${proj.profiles?.plan || 'free'}`}>{proj.profiles?.plan || 'free'}</span>
                    <span>·</span>
                    <span>{proj.category}</span>
                    <span>·</span>
                    <span>{proj.submitted_at ? new Date(proj.submitted_at).toLocaleDateString() : new Date(proj.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <StatusBadge status={proj.status} />
              </div>
            ))}
          </div>

          {selected && (
            <div className="admin-detail card">
              <div className="admin-detail-header">
                <h3>{selected.title}</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Link to={`/project/${selected.id}`} target="_blank" className="btn btn-ghost btn-sm">View project →</Link>
                  <button className="btn btn-ghost btn-sm" onClick={() => setSelected(null)}>✕</button>
                </div>
              </div>

              <div className="admin-detail-fields">
                <div className="admin-field"><span>Creator</span><span>{selected.profiles?.display_name || selected.profiles?.full_name}</span></div>
                <div className="admin-field"><span>Email</span><span>{selected.profiles?.email}</span></div>
                <div className="admin-field"><span>Plan</span><PlanBadge plan={selected.profiles?.plan || 'free'} /></div>
                <div className="admin-field"><span>Category</span><span>{selected.category}{selected.custom_category ? ` — ${selected.custom_category}` : ''}</span></div>
                <div className="admin-field"><span>Visibility</span><span>{selected.visibility}</span></div>
                <div className="admin-field"><span>Unique ID</span><code>{selected.unique_id}</code></div>
                <div className="admin-field"><span>Submitted</span><span>{selected.submitted_at ? new Date(selected.submitted_at).toLocaleDateString() : '—'}</span></div>
              </div>

              {selected.description && (
                <div className="admin-section">
                  <label>Description</label>
                  <p>{selected.description}</p>
                </div>
              )}

              <div className="admin-section">
                <label>Commit history ({commits.length} update{commits.length !== 1 ? 's' : ''})</label>
                {commitsLoading ? (
                  <div style={{ textAlign: 'center', padding: 20 }}><span className="loading-spinner" /></div>
                ) : commits.length === 0 ? (
                  <p style={{ color: 'var(--text-3)', fontSize: 13 }}>No commits on this project.</p>
                ) : (
                  <div className="timeline" style={{ marginTop: 12 }}>
                    {commits.map(c => (
                      <div key={c.id} className="timeline-item">
                        <div className="timeline-dot" />
                        <div className="timeline-meta">{new Date(c.created_at).toLocaleString()} · {c.commit_type}</div>
                        <div className="timeline-msg">{c.message}</div>
                        {c.commit_type === 'photo' && c.media_url && (
                          <img src={c.media_url} alt="commit" className="timeline-photo" />
                        )}
                        {c.commit_type === 'link' && c.media_url && (
                          <a href={c.media_url} target="_blank" rel="noopener noreferrer" className="timeline-link">🔗 {c.media_url}</a>
                        )}
                        {c.commit_type === 'video' && c.media_url && (
                          <VideoEmbed url={c.media_url} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="admin-section">
                <label>Message to creator</label>
                <textarea rows={3} placeholder="Write a message..." value={message} onChange={e => setMessage(e.target.value)} />
              </div>

              <div className="admin-actions">
                <button className="btn btn-primary btn-sm" disabled={actionLoading} onClick={() => handleAction('verify')}>
                  {actionLoading ? <span className="loading-spinner" /> : '✓ Verify — Human Origin Confirmed'}
                </button>
                <button className="btn btn-secondary btn-sm" disabled={actionLoading} onClick={() => handleAction('needs_evidence')}>
                  Request More Evidence
                </button>
                <button className="btn btn-danger btn-sm" disabled={actionLoading} onClick={() => handleAction('reject')}>
                  Reject
                </button>
              </div>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  )
}
